import type { Routes } from '@angular/router';

import { notLoggedGuard } from '@core/canmatch/not-logged.match';
import { legacyJourneyGuard, notLegacyJourneyGuard } from '@core/canmatch/user-journey.match';
import { canAccessDashboardGuard, noOrderGuard, noOrderInProgressGuard } from '@core/guards/can-access-dashboard.guard';
import { canAccessOrderTunnelGuard } from '@core/guards/can-access-order-tunnel.guard';
import { CanValidateIbanGuard } from '@core/guards/can-validate-iban.guard';
import { hasOrderGuard } from '@core/guards/has-order.guard';
import { FeaturesRoutingEnum } from '@features/features-routing.enum';
import { DefaultLayoutComponent } from '@features/layouts/default-layout/default-layout.component';
import { isEligibleGuard, orderInProgressGuard } from '@features/order/steps.resolver';

export const FEATURES_ROUTES: Routes = [
  {
    path: '',
    component: DefaultLayoutComponent,
    canMatch: [notLoggedGuard],
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: FeaturesRoutingEnum.Dashboard,
      },
      {
        path: '',
        canMatch: [legacyJourneyGuard],
        children: [
          {
            path: FeaturesRoutingEnum.Dashboard,
            loadChildren: () => import('./dashboard/routes').then((module) => module.DASHBOARD_ROUTES),
          },
          {
            path: FeaturesRoutingEnum.DossierDetail,
            loadChildren: () => import('./dossier-details/routes').then((module) => module.DOSSIER_DETAILS_ROUTES),
          },
          {
            path: FeaturesRoutingEnum.ValidateIban,
            loadChildren: () => import('./validate-iban/routes').then((module) => module.VALIDATE_IBAN_ROUTES),
            canActivate: [CanValidateIbanGuard],
          },
          {
            path: FeaturesRoutingEnum.StartComplementaryForm,
            loadChildren: () =>
              import('./start-complementary-form/routes').then((module) => module.START_COMPLEMENTARY_FORM_ROUTES),
          },
          {
            path: FeaturesRoutingEnum.NpsFeedback,
            loadChildren: () => import('./nps-feedback/routes').then((module) => module.NPS_FEEDBACK_ROUTES),
          },
          {
            path: FeaturesRoutingEnum.HelpForm,
            loadChildren: () => import('./help-form/routes').then((module) => module.HELP_FORM_ROUTES),
          },
          {
            path: FeaturesRoutingEnum.DepotForm,
            loadChildren: () => import('./depot-form/routes').then((module) => module.DEPOT_FORM_ROUTES),
          },
          {
            path: FeaturesRoutingEnum.UpdateData,
            loadChildren: () => import('./update-data/routes').then((module) => module.UPDATE_DATA_ROUTES),
          },
          {
            path: FeaturesRoutingEnum.Partner,
            canActivate: [],
            loadChildren: () => import('./partner/routes').then((module) => module.PARTNER_ROUTES),
          },
        ],
      },
      {
        path: '',
        canMatch: [notLegacyJourneyGuard],
        children: [
          {
            path: FeaturesRoutingEnum.Dashboard,
            loadChildren: () => import('./orders/routes').then((module) => module.ORDERS_ROUTES),
            canActivate: [canAccessDashboardGuard],
          },
          {
            path: FeaturesRoutingEnum.NpsFeedback,
            loadChildren: () => import('./nps-feedback/routes').then((module) => module.NPS_FEEDBACK_ROUTES),
          },
          {
            path: FeaturesRoutingEnum.Promise,
            loadChildren: () => import('./promise/routes').then((module) => module.PROMISE_ROUTES),
            canActivate: [canAccessOrderTunnelGuard, noOrderGuard, isEligibleGuard],
          },
          {
            path: FeaturesRoutingEnum.Order,
            loadChildren: () => import('./order/routes').then((module) => module.ORDER_ROUTES),
            canActivateChild: [canAccessOrderTunnelGuard, isEligibleGuard, orderInProgressGuard],
          },
          {
            path: FeaturesRoutingEnum.HelpForm,
            canActivate: [noOrderInProgressGuard],
            loadChildren: () => import('./help-form/routes').then((module) => module.HELP_FORM_ROUTES),
          },
          {
            path: FeaturesRoutingEnum.DepotForm,
            canActivate: [noOrderInProgressGuard],
            loadChildren: () => import('./depot-form/routes').then((module) => module.DEPOT_FORM_ROUTES),
          },
          {
            path: FeaturesRoutingEnum.UpdateData,
            canActivate: [noOrderInProgressGuard],
            loadChildren: () => import('./update-data/routes').then((module) => module.UPDATE_DATA_ROUTES),
          },
          {
            path: FeaturesRoutingEnum.Chatbot,
            canActivate: [hasOrderGuard],
            loadChildren: () => import('./chatbot/routes').then((module) => module.CHATBOT_ROUTES),
          },
          {
            path: FeaturesRoutingEnum.Partner,
            canActivate: [],
            loadChildren: () => import('./partner/routes').then((module) => module.PARTNER_ROUTES),
          },
        ],
      },
    ],
  },
  {
    path: FeaturesRoutingEnum.Banking,
    loadChildren: () => import('./banking/routes').then((module) => module.BANKING_ROUTES),
  },
  {
    path: '**',
    loadComponent: () => import('./not-found/not-found.component').then((module) => module.NotFoundComponent),
  },
];
